import React, { useMemo } from "react";
import { useConsole } from "../lib/console";
import { useNow } from "../lib/hooks";
import { SEV_RANK, attackLabel, attackVar, fmtTime, num, plural, sevLabel, sevVar, timeAgo } from "../lib/format";
import { VULNS, isPortalAttack, vulnKey } from "../lib/vulns";
import { Card, Empty, SevBadge } from "./ui";
import { ActivityChart, BarList } from "./charts";
import "./WebsiteWatch.css";

const live = (c) => (c.threats || []).filter((t) => t.is_threat !== false);

/** Who is signed in to the bank portal, and when they arrived or left. */
export function PortalPresence() {
  const { portal } = useConsole();
  const now = useNow(5000);
  const active = portal?.active || [];
  const events = (portal?.events || []).slice(0, 6);
  return (
    <Card title="Bank portal customers" subtitle="Real-time sign-ins to the banking site" icon="user">
      {active.length ? (
        <ul className="pp-active">
          {active.map((s) => (
            <li key={`${s.username}-${s.since}`}>
              <i className="pp-dot" aria-hidden="true" />
              <div>
                <strong>{s.username} is signed in</strong>
                <span>
                  {[s.city, s.country].filter(Boolean).join(", ") || "Unknown location"} · <span className="mono">{s.ip}</span> · since {fmtTime(s.since, false)}
                </span>
                <em>Normal activity: a registered customer using the site as expected.</em>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="pp-none">No customer is signed in right now.</p>
      )}
      {events.length > 0 && (
        <ul className="pp-log" aria-label="Recent sign-in activity">
          {events.map((e) => (
            <li key={e.id}>
              <span className={e.event === "signed_in" ? "pp-in" : e.event === "alerted" ? "pp-warn" : "pp-out"}>{e.event === "signed_in" ? "Signed in" : e.event === "alerted" ? "Customer alerted" : "Left the site"}</span>
              <span>
                <b>{e.username}</b>
                {e.event !== "signed_in" && e.reason ? ` (${e.reason})` : ""}
              </span>
              <time>{timeAgo(e.at, now)}</time>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Detections per 10 seconds over the last minutes. */
export function TrafficCard() {
  const { timeline } = useConsole();
  return (
    <Card title="Detections over time" subtitle="Per 10 seconds, live" icon="activity">
      <ActivityChart timeline={timeline} metric="threats" height={210} />
    </Card>
  );
}

/** Severity split as a ring with a plain-text legend. */
export function SeverityCard() {
  const c = useConsole();
  const rows = useMemo(() => {
    const m = {};
    live(c).forEach((t) => {
      const s = String(t.severity || "LOW").toUpperCase();
      m[s] = (m[s] || 0) + 1;
    });
    return Object.entries(m)
      .sort((a, b) => (SEV_RANK[b[0]] || 0) - (SEV_RANK[a[0]] || 0))
      .map(([key, value]) => ({ key, value, label: sevLabel(key) }));
  }, [c.threats]);
  const total = rows.reduce((s, r) => s + r.value, 0);
  const R = 52;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <Card title="Severity" subtitle="Detections in the live window" icon="gauge">
      {!total ? (
        <Empty compact icon="gauge">Severity appears as detections arrive.</Empty>
      ) : (
        <div className="sv">
          <svg viewBox="0 0 140 140" role="img" aria-label={`${total} detections by severity`}>
            <circle cx="70" cy="70" r={R} fill="none" stroke="var(--line)" strokeWidth="14" />
            {rows.map((r) => {
              const len = (r.value / total) * C;
              const el = (
                <circle key={r.key} cx="70" cy="70" r={R} fill="none" stroke={sevVar(r.key)} strokeWidth="14" strokeDasharray={`${Math.max(0, len - 2)} ${C}`} strokeDashoffset={-offset} transform="rotate(-90 70 70)" />
              );
              offset += len;
              return el;
            })}
            <text x="70" y="68" textAnchor="middle" className="sv-num">{num(total)}</text>
            <text x="70" y="86" textAnchor="middle" className="sv-sub">detections</text>
          </svg>
          <ul>
            {rows.map((r) => (
              <li key={r.key}>
                <span className="sev-tag" style={{ "--tone": sevVar(r.key) }}>{r.label.toUpperCase()}</span>
                <b>{num(r.value)}</b>
                <small>{Math.round((r.value / total) * 100)}%</small>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

export function AttackTypesCard({ onSelect }) {
  const c = useConsole();
  const items = useMemo(() => {
    const m = {};
    live(c).forEach((t) => (m[t.attack_type] = (m[t.attack_type] || 0) + 1));
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([key, value]) => ({ key, value, label: attackLabel(key), color: attackVar(key) }));
  }, [c.threats]);
  return (
    <Card title="Attack types" subtitle="Most common in the live window" icon="barChart">
      <BarList items={items} onSelect={onSelect} max={7} empty="No attacks yet" />
    </Card>
  );
}

export function CountriesCard() {
  const c = useConsole();
  const items = useMemo(() => {
    const m = {};
    live(c).forEach((t) => t.country && t.country !== "Unknown" && (m[t.country] = (m[t.country] || 0) + 1));
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .map(([key, value]) => ({ key, value, label: key }));
  }, [c.threats]);
  return (
    <Card title="Top attacking countries" subtitle="Where detections originate" icon="globe">
      <BarList items={items} max={7} empty="No attacks yet" />
    </Card>
  );
}

/** Weaknesses the observed attacks aim at, with CWE and OWASP references. */
export function VulnerabilityTable() {
  const c = useConsole();
  const rows = useMemo(() => {
    const m = {};
    live(c).forEach((t) => {
      const key = vulnKey(t);
      if (!VULNS[key]) return;
      const r = (m[key] ||= { key, count: 0, ips: new Set(), blocked: new Set(), sev: "LOW", portal: 0 });
      r.count += 1;
      r.ips.add(t.source_ip);
      if (t.contained || c.blockedIPs.has(t.source_ip)) r.blocked.add(t.source_ip);
      if ((SEV_RANK[String(t.severity).toUpperCase()] || 0) > (SEV_RANK[r.sev] || 0)) r.sev = String(t.severity).toUpperCase();
      if (isPortalAttack(t)) r.portal += 1;
    });
    return Object.values(m).sort((a, b) => (SEV_RANK[b.sev] - SEV_RANK[a.sev]) || b.count - a.count);
  }, [c.threats, c.blockedIPs]);
  const portal = rows.reduce((s, r) => s + r.portal, 0);
  return (
    <Card
      title="Vulnerabilities targeted"
      subtitle="Weaknesses that the detected attacks try to exploit, mapped to CWE and the OWASP Top 10 (2021)"
      icon="bug"
      flush
      footer={
        <>
          <span>
            {plural(rows.length, "vulnerability class", "vulnerability classes")} seen. {portal ? `${plural(portal, "attempt")} came from the bank sign-in page. ` : ""}Counts include simulated traffic.
          </span>
        </>
      }
    >
      {!rows.length ? (
        <Empty compact icon="bug">No attacks against known weaknesses yet.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table vt">
            <thead>
              <tr>
                <th>Vulnerability</th>
                <th>Reference</th>
                <th>Worst severity</th>
                <th className="num">Attempts</th>
                <th className="num">Sources</th>
                <th>Blocked</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const v = VULNS[r.key];
                return (
                  <tr key={r.key}>
                    <td>
                      <strong>{v.name}</strong>
                      <small>{v.weakness}</small>
                      <small className="vt-fix">Fix: {v.fix}</small>
                    </td>
                    <td className="mono vt-ref">
                      {v.cwe || "—"}
                      <small>{v.owasp || "—"}</small>
                    </td>
                    <td><SevBadge sev={r.sev} /></td>
                    <td className="num">{num(r.count)}</td>
                    <td className="num">{num(r.ips.size)}</td>
                    <td>{r.blocked.size} of {r.ips.size}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
